import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { formDataService } from "@/services";
import { videoService } from "@/services/videoService";
import useAuth from "@/hooks/stores/useAuth";
import FormSidebar from "./FormSidebar";
import AudioPlayer from "@/components/AudioPlayer";
import { CheckCircle, Volume2, Loader2, AlertCircle } from "lucide-react";
import Footer from "@/components/Footer";
import useFormSidebar from "@/hooks/stores/useFormSidebar";

const DynamicForm = ({ formData }) => {
  const navigate = useNavigate();
  const userId = useAuth((state) => state.userId);
  const userFlow = useAuth((state) => state.userFlow);
  const { isSidebarOpen, closeSidebar } = useFormSidebar();
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const formValuesRef = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [formName, setFormName] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const saveInProgress = useRef(false);
  const autoSaveTimeoutRef = useRef(null);
  const hasInitialized = useRef(false);
  const hasAudioFetched = useRef(false);

  // Fetch audio for remote users
  useEffect(() => {
    const fetchAudioForRemoteUser = async () => {
      // Prevent multiple API calls
      if (userFlow !== 'remote' || !userId || hasAudioFetched.current) return;
      
      hasAudioFetched.current = true;
      setAudioLoading(true);
      setAudioError(null);
      
      try {
        // Try to get audio URL from user videos API
        const audioFilename = await videoService.getUserAudioUrl(userId);
        
        if (audioFilename) {
          // Generate the streaming URL for the audio
          const streamUrl = await videoService.getAudioStreamUrl(audioFilename);
          setAudioUrl(streamUrl);
        } else {
          console.log('No audio found for remote user');
          setAudioError('No audio available for your session');
        }
      } catch (error) {
        console.error('Error fetching audio for remote user:', error);
        setAudioError('Failed to load audio');
      } finally {
        setAudioLoading(false);
      }
    };

    fetchAudioForRemoteUser();
  }, [userFlow, userId]);

  // Initialize form data
  useEffect(() => {
    if (!formData?.form_form_steps || hasInitialized.current) return;

    const initializeFormData = async () => {
      hasInitialized.current = true;
      setIsLoading(true);
      try {
        // Initialize with default values
        const newFormValues = {};
        formData.form_form_steps.forEach((step) => {
          step.form_step_form_sections.forEach((section) => {
            const repeatCount = Math.max(
              parseInt(section.default_repeat_count) || 1,
              1
            );
            let rowValues = [];

            for (let rowIndex = 0; rowIndex < repeatCount; rowIndex++) {
              let rowObj = {};
              section.form_step_form_fields.forEach((field) => {
                const defaultValue = field.form_section_defaults?.find(
                  (def) =>
                    def.row_index === rowIndex && def.form_field_id === field.id
                );
                rowObj[field.id] = defaultValue
                  ? defaultValue.default_value
                  : "";
              });
              rowValues.push(rowObj);
            }
            newFormValues[section.id] = rowValues;
          });
        });

        // Fetch and merge user submission data
        try {
          const responseJson = await formDataService.getUserData(userId);
          // console.log("User data response:", responseJson);

          if (responseJson.success && responseJson.data.form_submissions) {
            setFormName(responseJson.data.form_submissions.form.name);
            const submissionValues = responseJson.data.form_submissions.submission_values;

            // console.log("Submission values:", submissionValues);

            // Group submission values by section and row index
            // First, we need to map field IDs to sections using the form structure
            const fieldToSectionMap = {};
            formData.form_form_steps.forEach((step) => {
              step.form_step_form_sections.forEach((section) => {
                section.form_step_form_fields.forEach((field) => {
                  fieldToSectionMap[field.id] = section.id;
                });
              });
            });

            // console.log("Field to section map:", fieldToSectionMap);

            const valuesBySection = {};
            submissionValues.forEach((submission) => {
              // Use the field to section mapping since section is null in backend
              const sectionId = fieldToSectionMap[submission.field_id];
              const rowIndex = submission.section_repeat_index ?? 0;
              
              if (sectionId) {
                if (!valuesBySection[sectionId]) {
                  valuesBySection[sectionId] = {};
                }
                if (!valuesBySection[sectionId][rowIndex]) {
                  valuesBySection[sectionId][rowIndex] = {};
                }
                valuesBySection[sectionId][rowIndex][submission.field_id] = submission.value;
              }
            });

            // console.log("Values by section:", valuesBySection);

            // Merge submission values with default values
            formData.form_form_steps.forEach((step) => {
              step.form_step_form_sections.forEach((section) => {
                let repeatCount = Math.max(
                  parseInt(section.default_repeat_count) || 1,
                  1
                );
                if (valuesBySection[section.id]) {
                  repeatCount = Math.max(
                    repeatCount,
                    Object.keys(valuesBySection[section.id]).length
                  );
                }

                const rowData = newFormValues[section.id] || [];

                for (let rowIndex = 0; rowIndex < repeatCount; rowIndex++) {
                  const rowInstance = rowData[rowIndex] || {};
                  const rowSubmission = valuesBySection[section.id]?.[rowIndex] || {};

                  section.form_step_form_fields.forEach((field) => {
                    if (rowSubmission.hasOwnProperty(field.id)) {
                      rowInstance[field.id] = rowSubmission[field.id];
                    }
                  });
                  rowData[rowIndex] = rowInstance;
                }
                newFormValues[section.id] = rowData;
              });
            });
          }
        } catch (error) {
          // console.log("No existing user data found, using defaults");
        }

        setFormValues(newFormValues);
        formValuesRef.current = newFormValues;
      } catch (error) {
        console.error("Error initializing form data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFormData();
  }, [formData, userId]);

  // Handle field changes
  const handleChange = (sectionId, field, value, index = 0) => {
    // Clear validation error for this field
    if (validationErrors[`${sectionId}_${field.id}_${index}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${sectionId}_${field.id}_${index}`];
        return newErrors;
      });
    }

    setFormValues((prevValues) => {
      const sectionValues = prevValues[sectionId] || [];
      const updatedSectionValues = [...sectionValues];

      if (!updatedSectionValues[index]) {
        updatedSectionValues[index] = {};
      }

      updatedSectionValues[index] = {
        ...updatedSectionValues[index],
        [field.id]: value,
      };

      const newFormValues = {
        ...prevValues,
        [sectionId]: updatedSectionValues,
      };

      formValuesRef.current = newFormValues;
      return newFormValues;
    });

    setHasUnsavedChanges(true);

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Auto-save with debounce
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await saveStepData(currentStep, formValuesRef.current);
        if (result === true) {
          const time = new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          setLastSaveTime(time);
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error("Error in auto-save:", error);
        // toast.error("Error saving data");
      }
    }, 1000);
  };

  // Save step data
  const saveStepData = async (stepIndex, formValuesOverride = null) => {
    const step = formData.form_form_steps[stepIndex];
    const fieldsData = [];

    step.form_step_form_sections.forEach((section) => {
      const valuesToUse = formValuesOverride || formValues;
      const repeatedRows = valuesToUse[section.id] || [];
      repeatedRows.forEach((rowData, sectionRepeatIndex) => {
        Object.keys(rowData).forEach((fieldId) => {
          const value = rowData[fieldId];
          fieldsData.push({
            field_id: parseInt(fieldId),
            value: value || "",
            skip: 0
          });
        });
      });
    });

    try {
      const result = await formDataService.saveStepData(formData.id, userId, fieldsData);
      return result.success || result.status === "success";
    } catch (err) {
      console.error("Error saving step data:", err);
      return false;
    }
  };

  // Scroll to top whenever step changes
  useEffect(() => {
    // Use setTimeout to ensure DOM updates are complete before scrolling
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, [currentStep]);

  // Navigation handlers
  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const isStepComplete = (stepIndex) => {
    const step = formData.form_form_steps[stepIndex];
    if (!step) return false;

    let isComplete = true;

    step.form_step_form_sections.forEach((section) => {
      const sectionValues = formValues[section.id] || [];

      // If there are no values for this section and it's required, step is incomplete
      if (sectionValues.length === 0) {
        const hasRequiredFields = section.form_step_form_fields.some(
          (field) => field.required === 1
        );
        if (hasRequiredFields) {
          isComplete = false;
          return;
        }
      }

      // Check each row in the section
      sectionValues.forEach((rowInstance) => {
        section.form_step_form_fields.forEach((field) => {
          // Skip if field is hidden
          if (field.hidden) return;

          // Only check required fields
          if (field.required === 1) {
            const value = rowInstance[field.id];
            // Check if the value is empty
            if (
              value === undefined ||
              value === null ||
              (typeof value === "string" && value.trim() === "") ||
              (field.field_type === "checkbox" && value !== "1") ||
              (field.field_type === "radio" && !value)
            ) {
              isComplete = false;
            }
          }
        });
      });
    });

    return isComplete;
  };

  const handleSaveAndNext = () => {
    handleNext();
  };

  // Render field based on type
  const renderField = (field, sectionId, value, index) => {
    const fieldKey = `${sectionId}_${field.id}_${index}`;
    const error = validationErrors[fieldKey];

    const inputBaseClass = `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-foreground bg-white ${
      error ? 'border-red-500' : 'border-secondary-border'
    }`;

    switch (field.field_type) {
      case "text":
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(sectionId, field, e.target.value, index)}
            className={inputBaseClass}
            placeholder={field.placeholder || ""}
          />
        );

      case "textarea":
        return (
          <textarea
            value={value || ""}
            onChange={(e) => handleChange(sectionId, field, e.target.value, index)}
            rows={field.rows || 4}
            className={inputBaseClass}
            placeholder={field.placeholder || ""}
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={value || ""}
            onChange={(e) => handleChange(sectionId, field, e.target.value, index)}
            className={inputBaseClass}
            placeholder={field.placeholder || ""}
          />
        );

      case "date":
        return (
          <input
            type="date"
            value={value || ""}
            onChange={(e) => handleChange(sectionId, field, e.target.value, index)}
            className={inputBaseClass}
          />
        );

      case "radio":
        const options = field.options ? JSON.parse(field.options) : [];
        return (
          <div className="space-y-3">
            {options.map((option) => {
              const isExactMatch = value === option.value;
              const normalizedStoredValue = String(value).trim().replace(/\.$/, '').toLowerCase();
              const normalizedOptionValue = String(option.value).trim().replace(/\.$/, '').toLowerCase();
              const isNormalizedMatch = normalizedStoredValue === normalizedOptionValue;
              const isChecked = isExactMatch || isNormalizedMatch;
              
              return (
                <div key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    id={`${field.id}-${option.value}-${index}`}
                    value={option.value}
                    checked={isChecked}
                    onChange={(e) => handleChange(sectionId, field, e.target.value, index)}
                    className="h-4 w-4 text-primary focus:ring-primary border-secondary-border"
                  />
                  <label
                    htmlFor={`${field.id}-${option.value}-${index}`}
                    className="ml-3 text-foreground font-medium"
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case "checkbox":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={value === "1"}
              onChange={(e) => handleChange(sectionId, field, e.target.checked ? "1" : "0", index)}
              className="h-4 w-4 text-primary focus:ring-primary border-secondary-border rounded"
            />
            <label className="ml-3 text-foreground font-medium">
              {field.checkbox_label || "Select this option"}
            </label>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(sectionId, field, e.target.value, index)}
            className={inputBaseClass}
            placeholder={field.placeholder || ""}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Loading Your Form</h3>
          <p className="text-muted-foreground">Please wait while we prepare your form...</p>
        </div>
      </div>
    );
  }

  const currentStepData = formData.form_form_steps[currentStep];

  return (
    <div 
      className="min-h-screen bg-background"
      style={{
        overscrollBehavior: 'auto'
      }}
    >
            <Toaster position="top-right" />

              {/* Sidebar */}
        <FormSidebar
          isOpen={isSidebarOpen}
          close={closeSidebar}
          formName={formName}
          formData={formData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          completedSteps={completedSteps}
        />

        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-foreground bg-opacity-50 z-30 lg:hidden"
            onClick={closeSidebar}
          />
        )}
      
      {/* Save indicator */}
      {lastSaveTime && (
        <div className="fixed bottom-4 right-4 bg-primary rounded-lg px-4 py-3 flex items-center space-x-2 z-30 shadow-sm">
          <CheckCircle className="w-4 h-4 text-white" />
          <span className="text-sm font-medium text-white">
            Saved: {lastSaveTime}
          </span>
        </div>
      )}

      {/* Main Content */}
      <div 
        className="lg:ml-80"
        style={{
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-8">
        
        {/* Audio Player for Remote Users - Always at the top */}
        {userFlow === 'remote' && (
          <div className="mb-8">
            <div className="bg-secondary border border-secondary-border rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200">
              {/* Header */}
              <div className="flex items-center p-6 pb-4 border-b border-secondary-border">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg mr-4 flex-shrink-0">
                  <Volume2 className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-semibold text-foreground">Audio Recording</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">Listen to your recorded session</p>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 pt-5">
                {audioLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                      <p className="text-base font-medium text-foreground">Loading audio...</p>
                      <p className="text-sm text-muted-foreground mt-1">Please wait while we prepare your session recording</p>
                    </div>
                  </div>
                ) : audioError ? (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-5">
                    <div className="flex items-start">
                      <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full mr-3 mt-0.5 flex-shrink-0">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-red-800">Audio Unavailable</p>
                        <p className="text-sm text-red-600 mt-1">{audioError}</p>
                        <p className="text-xs text-red-500 mt-2">Please try refreshing the page or contact support if the issue persists.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-secondary-border/50 p-4 sm:p-5">
                    <AudioPlayer audioUrl={audioUrl} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="bg-secondary border border-secondary-border rounded-xl p-8 mb-6">
          <h2 className="text-2xl font-bold text-primary mb-4">
            {currentStepData?.title || formName || "My Form"}
          </h2>
          {currentStepData?.description && (
            <div className="text-muted-foreground">
              {currentStepData.description.split('/n').map((line, index) => (
                <React.Fragment key={index}>
                  <p className={`${index > 0 ? 'mt-1' : ''}`}>{line}</p>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Form content */}
        <div className="bg-secondary border border-secondary-border rounded-xl p-8">
          {currentStepData.form_step_form_sections.map((section) => (
            <div key={section.id} className="mb-10">
              <h3 className="text-xl font-bold text-foreground mb-6">
                {section.name}
              </h3>
              {section.description && (
                <p className="text-muted-foreground mb-6">{section.description}</p>
              )}

              {(formValues[section.id] || []).map((rowInstance, index) => (
                <div key={index} className="bg-secondary p-6 mb-6">
                  <div className="space-y-6">
                    {section.form_step_form_fields.map((field) => (
                      <div key={field.id} className="space-y-3">
                        <label className="block font-semibold text-foreground">
                          {field.name}
                          {field.required === 1 && (
                            <span className="text-primary ml-1">*</span>
                          )}
                        </label>
                        
                        {renderField(field, section.id, rowInstance[field.id], index)}
                        
                        {validationErrors[`${section.id}_${field.id}_${index}`] && (
                          <p className="text-red-500 text-sm font-medium">
                            {validationErrors[`${section.id}_${field.id}_${index}`]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Navigation */}
          <div className="flex justify-between items-center pt-8 border-t border-secondary-border mt-8">
            {currentStep > 0 ? (
              <button
                type="button"
                onClick={handlePrevious}
                className="px-6 py-3 border border-secondary-border text-muted-foreground rounded-lg hover:text-foreground hover:bg-background transition-colors font-medium"
              >
                ← Previous
              </button>
            ) : (
              <div />
            )}

            {currentStep < formData.form_form_steps.length - 1 && (
              <button
                type="button"
                onClick={handleSaveAndNext}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium shadow-sm"
              >
                Next →
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicForm; 